import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { useToast } from 'components/ui/use-toast';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { FEATURE_LABELS, PLAN_LABELS, entitlementsApi } from 'modules/entitlements';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import { useUpgradeDialogStore } from 'store/upgradeDialog';

// Subscription states that mean a paid plan lapsed (vs. a free org that simply
// never had the feature). A FEATURE_NOT_ENTITLED error under one of these reads
// as "your plan expired" instead of "this feature isn't in your plan". 'none'
// is intentionally excluded — a free org isn't "expired".
const EXPIRED_STATUSES = new Set(['past_due', 'unpaid', 'canceled']);

type Reason = 'limit' | 'expired' | 'feature';

// Delay before opening the billing dialog after the limit dialog closes. Long
// enough for Radix to tear down the first dialog's scroll-lock/overlay, short
// enough to feel like one continuous hand-off. See goToBilling.
const DIALOG_HANDOFF_MS = 150;

/** Mounted once in `Main.tsx`; reads the Zustand store the Axios interceptor dispatches to. UX only. */
export function GlobalUpgradeDialog() {
  const { t } = useTranslation();
  const { open: openSettings } = useSettingsDialog();
  const { toast } = useToast();
  const { state } = useAuthContext();
  const canBill = usePermission(PERMISSIONS.BILLING_EDIT);
  const { isOpen, payload, close } = useUpgradeDialogStore();

  const requestUpgrade = useMutation({
    mutationFn: async () =>
      (await entitlementsApi.RequestUpgrade({ requestedPlan: payload?.upgrade?.currentPlan })).data,
    onSuccess: () => {
      toast.success(t('upgrade_request_sent', 'Upgrade request sent'));
      close();
    },
    onError: () => {
      toast.error(t('something_went_wrong', 'Something went wrong'));
    },
  });

  if (!payload) return null;

  const status = state.entitlements?.subscriptionStatus ?? state.organization?.subscriptionStatus;
  const planKey = payload.upgrade?.currentPlan ?? state.entitlements?.planKey ?? 'FREE';
  const planLabel = PLAN_LABELS[planKey] ?? state.entitlements?.planName ?? planKey;
  const featureLabel = payload.featureKey
    ? FEATURE_LABELS[payload.featureKey as keyof typeof FEATURE_LABELS] ?? payload.featureKey
    : '';

  // The Free plan (planKey EXPIRED) is a working plan: a gated feature there reads
  // as "not in your plan", not "your plan has expired". Only a lapsed paid status does.
  const isExpired =
    payload.code === 'FEATURE_NOT_ENTITLED' && EXPIRED_STATUSES.has(status ?? '');
  const reason: Reason =
    payload.code === 'USAGE_LIMIT_EXCEEDED' ? 'limit' : isExpired ? 'expired' : 'feature';

  const used = payload.upgrade?.used;
  const limit = payload.upgrade?.limit;
  const showUsage = reason === 'limit' && used != null && limit != null;

  let title: string;
  let description: string;
  if (reason === 'limit') {
    title = t('upgrade_dialog_limit_title', "You've reached your plan limit");
    description = t('upgrade_dialog_limit_desc', {
      plan: planLabel,
      defaultValue: `You've reached the ${planLabel} plan limit. Upgrade to keep going.`,
    });
  } else if (reason === 'expired') {
    title = t('upgrade_dialog_expired_title', 'Your plan has expired');
    description = t('upgrade_dialog_expired_desc', 'Renew or upgrade to restore access to your features.');
  } else if (payload.questionCount) {
    // Test activation blocked by question types the plan lacks: two ways out.
    title = t('upgrade_dialog_feature_title', "This feature isn't in your plan");
    description = t('upgrade_dialog_questions_desc', {
      count: payload.questionCount,
      plan: planLabel,
      defaultValue: `This test has ${payload.questionCount} question(s) of a type the ${planLabel} plan doesn't include. Upgrade, or remove them so students can take it.`,
    });
  } else if (featureLabel) {
    title = t('upgrade_dialog_feature_title', "This feature isn't in your plan");
    description = t('upgrade_dialog_feature_desc', {
      feature: featureLabel,
      plan: planLabel,
      defaultValue: `${featureLabel} isn't available on the ${planLabel} plan. Upgrade to unlock it.`,
    });
  } else {
    // Proactive nudge (e.g. the header "Upgrade plan" button) with no specific feature.
    title = t('upgrade_plan', 'Upgrade plan');
    description = t('upgrade_dialog_generic_desc', {
      plan: planLabel,
      defaultValue: `Upgrade from the ${planLabel} plan to unlock more.`,
    });
  }

  const goToBilling = () => {
    // Close the limit dialog first, THEN open the settings (billing) dialog on a
    // later tick. Both are Radix dialogs: flipping one closed and the other open
    // in the same commit makes their scroll-locks and overlays collide — two
    // stacked `bg-black/80` overlays leave billing behind a stuck black backdrop
    // (the modal-nesting trap). Sequencing hands scroll-lock + focus cleanly from
    // one dialog to the next.
    close();
    window.setTimeout(() => openSettings('billing'), DIALOG_HANDOFF_MS);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {showUsage && (
          <div className="space-y-2 rounded-lg border border-border bg-muted/40 px-3.5 py-3">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate font-medium text-foreground">
                {featureLabel || t('usage', 'Usage')}
              </span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold text-warning-700 dark:text-warning-400">{used}</span>
                <span className="text-muted-foreground"> / {limit}</span>
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-warning-500 transition-[width] duration-500 ease-out motion-reduce:transition-none"
                style={{
                  width: `${Math.min(100, Math.round((Number(used) / Math.max(1, Number(limit))) * 100))}%`,
                }}
              />
            </div>
          </div>
        )}

        {!canBill && (
          <p className="text-sm text-muted-foreground">
            {t('upgrade_dialog_admin_hint', 'Ask an organization admin to upgrade your plan.')}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t('dismiss', 'Dismiss')}
          </Button>
          {canBill ? (
            <Button onClick={goToBilling}>{t('upgrade_dialog_cta', 'View plans')}</Button>
          ) : (
            <Button onClick={() => requestUpgrade.mutate()} disabled={requestUpgrade.isPending}>
              {requestUpgrade.isPending && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              {t('request_upgrade', 'Request upgrade')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GlobalUpgradeDialog;
